<template>
  <div class="dashboard">
    <!-- Header Compartilhado -->
    <PageHeader 
      title="Dashboard de Chamados"
    />

    <!-- Container principal -->
    <main class="main-container">
      <!-- Barra de ações -->
      <div class="action-bar">
        <div class="search-wrapper">
          <svg class="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <input
            type="text"
            v-model="filtro"
            class="search-input"
            autocomplete="off"
            placeholder="Pesquisar chamados..."
            @click.stop.prevent="clear"
          />
        </div>
        <button class="btn-icon-secondary" @click="carregarChamados" :disabled="loading" title="Atualizar lista">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 10a7 7 0 1 1-2-4.9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M17 4v6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button v-if="!user?.isAdminDNWAYS" class="btn-primary" @click="abrirChamado">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 4v12M4 10h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span>Novo Chamado</span>
        </button>
      </div>

      <!-- Tabs de navegação -->
      <div class="tabs-container">
        <nav class="tabs">
          <a
            href="javascript:;"
            class="tab"
            :class="{ active: tab === 0 }"
            @click="selectTab(0)"
          >
            <span>Todos</span>
            <span class="tab-count">{{ objTarefas.length }}</span>
          </a>
          <a
            href="javascript:;"
            class="tab"
            :class="{ active: tab === 1 }"
            @click="selectTab(1)"
          >
            <span>Abertos</span>
            <span class="tab-count">{{ objTarefas.filter(t => t.status === 'Aberto').length }}</span>
          </a>
          <a
            href="javascript:;"
            class="tab"
            :class="{ active: tab === 2 }"
            @click="selectTab(2)"
          >
            <span>Fechados</span>
            <span class="tab-count">{{ objTarefas.filter(t => t.status === 'Fechado').length }}</span>
          </a>
          <a
            href="javascript:;"
            class="tab"
            :class="{ active: tab === 3 }"
            @click="selectTab(3)"
          >
            <span>Pendentes</span>
            <span class="tab-count">{{ objTarefas.filter(t => t.status === 'Pendente').length }}</span>
          </a>
        </nav>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-wrapper">
          <svg class="spinner" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" stroke="#00235C" stroke-width="4" stroke-linecap="round" stroke-dasharray="125" stroke-dashoffset="62.5"/>
          </svg>
        </div>
        <p class="loading-text">Carregando chamados...</p>
      </div>

      <!-- Lista de chamados -->
      <div v-else class="tickets-container">
        <div v-if="objTarefasFiltrado.length === 0" class="empty-state">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="32" fill="#F3F4F6"/>
            <path d="M32 20v24M20 32h24" stroke="#9CA3AF" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <h3>Nenhum chamado encontrado</h3>
          <p>Não há chamados correspondentes à sua busca.</p>
        </div>

        <div
          v-for="(obj, index) in objTarefasFiltrado"
          :key="index"
          class="ticket-card"
        >
          <div class="ticket-header">
            <div class="ticket-meta">
              <div class="meta-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM2 6h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ formatDate(obj.data) }}</span>
              </div>
              <div class="meta-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 4v4l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>#{{ obj.codigo }}</span>
              </div>
            </div>
            <span
              class="status-badge"
              :class="{
                'status-pending': obj.status === 'Pendente',
                'status-open': obj.status === 'Aberto',
                'status-closed': obj.status === 'Fechado'
              }"
            >
              {{ obj.status }}
            </span>
          </div>

          <div class="ticket-body">
            <h3 class="ticket-title">{{ obj.titulo }}</h3>
            <div v-if="obj.nomeProduto" class="ticket-product">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="2" width="12" height="10" rx="1" stroke="currentColor" stroke-width="1.2"/>
                <path d="M4 5h6M4 8h4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
              </svg>
              <span>{{ obj.nomeProduto }}</span>
            </div>
            <p class="ticket-description">{{ obj.descricao }}</p>
          </div>

          <div class="ticket-footer">
            <button class="btn-secondary" @click="verDetalhes(obj.guid)">
              <span>Ver detalhes</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 12l4-4-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
  <ModalAbrirChamado @chamado-criado="handleChamadoCriado" />
</template>

<script>
import { mapGetters } from 'vuex';
import ModalAbrirChamado from '../views/Modal/ModalAbrirChamado.vue';
import PageHeader from '@/components/PageHeader.vue';
import api from '@/api/axios';

export default {
    name: 'DashboardInicio',
      components: {
        ModalAbrirChamado,
        PageHeader
      },
      computed: {
        ...mapGetters({
            user: 'auth/user',
            perfilCliente: 'GET_PERFIL_CLIENTE',
        }),
      objTarefasFiltrado(){
        if(!this.filtro && this.tab == 0)
          return this.objTarefas;

        return this.objTarefas.filter(a => {
          const searchText = this.filtro.toLowerCase();
          const matchesSearch = !this.filtro || 
            a.titulo.toLowerCase().includes(searchText) || 
            a.codigo.toLowerCase().includes(searchText) ||
            a.descricao.toLowerCase().includes(searchText);
          
          const matchesTab = this.tab == 0 
            || (this.tab == 1 && a.status == 'Aberto') 
            || (this.tab == 2 && a.status == 'Fechado') 
            || (this.tab == 3 && a.status == 'Pendente');
          
          return matchesSearch && matchesTab;
        });
      }
    },
    data() {
        return {
            filtro: '',
            dadosUsuario: {
              PerfilRisco: ''
            },
            loginSession: {
                Usuario: null,
                Senha: null,
            },
            objTarefas: [],
            tab: 0,
            loading: false,
            error: null,
        };
    },
    mounted(){
      this.$store.commit("SET_ABRIR_MODAL_CHAMADO", false);
      this.dadosUsuario.nome = localStorage.getItem('nomeRazaoSocial');
      this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
      
      // Carrega os chamados do backend
      this.carregarChamados();
    },
    methods: {
      formatDate(date) {
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).replace(' de ', ' ');
      },
      verDetalhes(id){
          this.$router.push('/detalhes/' + id );
      },
      abrirChamado(){
        console.log('bate aqui');
        this.$store.commit("SET_ABRIR_MODAL_CHAMADO", true);
      },
      selectTab(tabSelected){
          this.tab = tabSelected;
        },
      async carregarChamados() {
        this.loading = true;
        this.error = null;
        
        try {
          // Backend já diferencia: Admin DNWAYS vê todos, outros usuários veem apenas os seus
          const response = await api.get('/chamado/meus-chamados', {
            withCredentials: true
          });
          
          // Mapeia os dados do backend para o formato do frontend
          this.objTarefas = response.data.map(chamado => ({
            guid: chamado.id || chamado.Id,
            codigo: chamado.id ? chamado.id.toString() : (chamado.Id ? chamado.Id.toString() : '0'),
            titulo: chamado.titulo || chamado.Titulo,
            descricao: chamado.descricao || chamado.Descricao,
            status: chamado.status || chamado.Status,
            data: new Date(chamado.dataAbertura || chamado.DataAbertura),
            prioridade: chamado.prioridade || chamado.Prioridade,
            nomeProduto: chamado.nomeProduto || chamado.NomeProduto,
            anexos: chamado.anexos || chamado.Anexos || []
          }));
          
          this.loading = false;
        } catch (error) {
          this.loading = false;
          console.error('Erro ao carregar chamados:', error);
          
          if (error.response?.status === 401) {
            this.$swal({
              icon: 'error',
              title: 'Sessão expirada',
              text: 'Por favor, faça login novamente.',
            }).then(() => {
              this.$router.push('/login');
            });
          } else {
            this.error = 'Erro ao carregar chamados. Tente novamente.';
            this.$swal({
              icon: 'error',
              title: 'Erro ao carregar chamados',
              text: error.response?.data?.message || error.response?.data?.Message || 'Ocorreu um erro ao buscar os chamados.',
            });
          }
        }
      },
      handleChamadoCriado() {
        // Recarrega a lista de chamados quando um novo é criado
        this.carregarChamados();
      },
      clear() {
        // Método para limpar o filtro se necessário
        // this.filtro = '';
      },
    }
};

</script>

<style lang="scss" scoped>

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* Reset e Variáveis */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  font-family: 'Inter', sans-serif;
}

/* Container Principal */
.main-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

/* Barra de Ações */
.action-bar {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
  flex: 1;
  min-width: 280px;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 3rem;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 0.875rem;
    transition: all 0.3s ease;
    background: white;

    &:focus {
      outline: none;
      border-color: #00235C;
      box-shadow: 0 0 0 3px rgba(0, 35, 92, 0.1);
    }

    &::placeholder {
      color: #9ca3af;
    }
  }
}

.btn-icon-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: white;
  color: #00235C;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    border-color: #00235C;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 35, 92, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #00235C 0%, #001a45 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 35, 92, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

/* Tabs */
.tabs-container {
  background: white;
  border-radius: 16px;
  padding: 0.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &.active {
    background: linear-gradient(135deg, #00235C 0%, #001a45 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(0, 35, 92, 0.2);
  }

  .tab-count {
    padding: 0.125rem 0.5rem;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
  }

  &:not(.active) .tab-count {
    background: #e5e7eb;
    color: #6b7280;
  }
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.spinner-wrapper {
  margin-bottom: 1.5rem;
}

.spinner {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
}

/* Container de Tickets */
.tickets-container {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
}

/* Estado Vazio */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;

  svg {
    margin-bottom: 1.5rem;
    opacity: 0.5;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.875rem;
    color: #6b7280;
  }
}

/* Card de Ticket */
.ticket-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px solid #e5e7eb;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: #00235C;
  }
}

.ticket-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.ticket-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 500;

  svg {
    color: #9ca3af;
  }
}

.status-badge {
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;

  &.status-open {
    background: #FEF3C7;
    color: #D97706;
  }

  &.status-closed {
    background: #D1FAE5;
    color: #059669;
  }

  &.status-pending {
    background: #DBEAFE;
    color: #2563EB;
  }
}

.ticket-body {
  flex: 1;
}

.ticket-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #00235C;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.ticket-product {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 0.5rem;

  svg {
    color: #0284c7;
    flex-shrink: 0;
  }
}

.ticket-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ticket-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 0.5rem;
}

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: transparent;
  color: #00235C;
  border: 2px solid #00235C;
  border-radius: 10px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #00235C;
    color: white;
    transform: translateX(4px);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(2px);
  }
}

/* Responsividade */
@media (max-width: 1024px) {
  .main-container {
    padding: 1.5rem;
  }

  .tickets-container {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .header-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 768px) {
  .header-content {
    padding: 1.25rem 1.5rem;
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .header-title {
    font-size: 1.375rem;
  }

  .btn-logoff {
    align-self: flex-end;
  }

  .main-container {
    padding: 1.25rem;
  }

  .action-bar {
    flex-direction: column;
  }

  .search-wrapper {
    min-width: 100%;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .tabs {
    justify-content: flex-start;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .tab {
    flex-shrink: 0;
  }

  .tickets-container {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .ticket-card {
    padding: 1.25rem;
  }

  .ticket-header {
    flex-direction: column;
    gap: 0.75rem;
  }

  .status-badge {
    align-self: flex-start;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 1rem;
  }

  .header-title {
    font-size: 1.25rem;
  }

  .main-container {
    padding: 1rem;
  }

  .tabs-container {
    padding: 0.375rem;
  }

  .tab {
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
  }

  .ticket-card {
    padding: 1rem;
  }

  .ticket-title {
    font-size: 1rem;
  }

  .ticket-meta {
    flex-direction: column;
    gap: 0.5rem;
  }

  .btn-primary,
  .btn-secondary {
    font-size: 0.8125rem;
    padding: 0.625rem 1.125rem;
  }
}

</style>